import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './api';

/**
 * Tên và biểu tượng của bản cài, áp vào thẻ trình duyệt.
 *
 * ── Vì sao áp lúc CHẠY chứ không nướng vào bản build ──────────────────────
 *
 * Người vận hành đổi tên trang trên panel; bắt build lại giao diện sau mỗi lần
 * đổi là biến một ô nhập thành một lượt deploy. `index.html` mang giá trị mặc
 * định, còn khối này ghi đè khi máy chủ trả về giá trị riêng.
 *
 * ── Vì sao gọi được khi CHƯA đăng nhập ────────────────────────────────────
 *
 * `/api/branding` là public có chủ đích: thẻ trình duyệt phải đúng ngay ở trang
 * giới thiệu. Nếu nó đòi phiên thì khách ghé thăm thấy tên mặc định rồi mới đổi
 * sau khi đăng nhập — đúng thứ tính năng này sinh ra để bỏ.
 */

export interface Branding {
  siteName: string;
  hasIcon: boolean;
  /** Mốc sửa gần nhất, đi vào query của ảnh để phá cache trình duyệt. */
  iconVersion: string;
}

/** Tên dùng khi người vận hành chưa đặt gì — khớp `<title>` trong `index.html`. */
const DEFAULT_NAME = 'GenSolva';

export function useBranding(): Branding | undefined {
  const { data } = useQuery({
    queryKey: ['branding'],
    queryFn: () => apiFetch<Branding>('/api/branding'),
    // Đổi rất hiếm, và một giá trị cũ ở đây chỉ là cái tên trên thẻ trình duyệt.
    staleTime: 5 * 60_000,
    retry: false,
  });
  return data;
}

/**
 * Gắn tên và biểu tượng vào tài liệu. Gọi MỘT lần, ở gốc cây React.
 *
 * Trả về giá trị để chỗ gọi khỏi phải hỏi lại bằng một query thứ hai.
 */
export function useApplyBranding(): Branding | undefined {
  const branding = useBranding();

  useEffect(() => {
    if (branding === undefined) return;

    document.title = branding.siteName.length > 0 ? branding.siteName : DEFAULT_NAME;

    // Favicon được trình duyệt cache rất dai — không đổi `href` thì đặt ảnh mới
    // xong vẫn thấy ảnh cũ cho tới lúc xoá cache tay. Mốc sửa trong query là thứ
    // duy nhất buộc nó tải lại.
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link === null) return;
    link.href = branding.hasIcon
      ? `/api/branding/icon?v=${encodeURIComponent(branding.iconVersion)}`
      : '/api/branding/icon';
  }, [branding]);

  return branding;
}
